// Cave84 — Locations hooks (Phase 1 Week 2)
// TanStack Query wrappers around services/locations.ts.
// Only the Home cellar has sub-locations; abroad/virtual cellars are flat.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { Location } from '@cave84/shared-types';
import {
  createLocation,
  deleteLocationRaw,
  listLocationsByCellar,
  updateLocation,
  type LocationCreateInput,
  type LocationUpdateInput
} from '@/services/locations';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') {
    throw new Error('Cave84: this mutation requires the owner role.');
  }
}

export function useCellarLocations(cellarId: string | undefined): UseQueryResult<Location[]> {
  return useQuery({
    queryKey: cellarId ? qk.locations.byCellar(cellarId) : qk.locations.byCellar('__noop__'),
    queryFn: () => (cellarId ? listLocationsByCellar(cellarId) : Promise.resolve([])),
    enabled: !!cellarId
  });
}

export function useCreateLocation(): UseMutationResult<Location, Error, LocationCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LocationCreateInput) => {
      assertOwner();
      return createLocation(input);
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: qk.locations.byCellar(input.cellar_id) });
    }
  });
}

export function useUpdateLocation(): UseMutationResult<
  void,
  Error,
  { id: string; cellarId: string; patch: LocationUpdateInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      assertOwner();
      await updateLocation(id, patch);
    },
    onSuccess: (_data, { cellarId }) => {
      void qc.invalidateQueries({ queryKey: qk.locations.byCellar(cellarId) });
    }
  });
}

export function useDeleteLocation(): UseMutationResult<
  void,
  Error,
  { id: string; cellarId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      assertOwner();
      await deleteLocationRaw(id);
    },
    onSuccess: (_data, { cellarId }) => {
      void qc.invalidateQueries({ queryKey: qk.locations.byCellar(cellarId) });
    }
  });
}
